import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, Facebook } from 'lucide-react';

export const Footer = () => {
    const currentYear = new Date().getFullYear();

    return (
        <footer className="bg-foreground text-white" data-testid="footer">
            {/* Wave decoration */}
            <div className="footer-wave transform rotate-180"></div>
            
            <div className="container-custom py-16">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
                    {/* Brand Column */}
                    <div className="space-y-4">
                        <Link to="/" className="flex items-center gap-2">
                            <img 
                                src="https://customer-assets.emergentagent.com/job_canine-training-pro/artifacts/k6jr7ouk_Laura%20logo%20CC.jpeg" 
                                alt="Craven Canines" 
                                className="h-12 w-auto bg-white rounded-lg p-1"
                            />
                            <span className="text-xl font-bold font-serif">
                                Craven Canines
                            </span>
                        </Link>
                        <p className="text-white/70 text-sm leading-relaxed">
                            Professional mantrailing sessions for dogs of all breeds and ages. 
                            Building confidence, focus, and teamwork through scent work.
                        </p>
                        <div className="flex gap-4 pt-2">
                            <a 
                                href="https://www.instagram.com/cravencanines" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-primary transition-colors duration-300"
                                data-testid="social-instagram"
                            >
                                <Instagram className="w-5 h-5" />
                            </a>
                            <a 
                                href="https://www.facebook.com/people/Craven-Canines/61561880805365/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-primary transition-colors duration-300"
                                data-testid="social-facebook"
                            >
                                <Facebook className="w-5 h-5" />
                            </a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="text-lg font-semibold mb-4 font-serif">Quick Links</h4>
                        <ul className="space-y-3">
                            <li>
                                <Link to="/" className="text-white/70 hover:text-primary transition-colors duration-200">
                                    Home
                                </Link>
                            </li>
                            <li>
                                <Link to="/mantrailing" className="text-white/70 hover:text-primary transition-colors duration-200">
                                    Mantrailing Sessions
                                </Link>
                            </li>
                            <li>
                                <Link to="/gallery" className="text-white/70 hover:text-primary transition-colors duration-200">
                                    Gallery
                                </Link>
                            </li>
                            <li>
                                <Link to="/faq" className="text-white/70 hover:text-primary transition-colors duration-200">
                                    FAQ
                                </Link>
                            </li>
                            <li>
                                <Link to="/gift-vouchers" className="text-white/70 hover:text-primary transition-colors duration-200">
                                    Gift Vouchers
                                </Link>
                            </li>
                            <li>
                                <Link to="/contact" className="text-white/70 hover:text-primary transition-colors duration-200">
                                    Contact
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div>
                        <h4 className="text-lg font-semibold mb-4 font-serif">Contact</h4>
                        <ul className="space-y-3">
                            <li className="flex items-center gap-3 text-white/70">
                                <Mail className="w-5 h-5 text-primary" />
                                <a href="mailto:infocravencanines@gmail.com" className="hover:text-primary transition-colors">
                                    infocravencanines@gmail.com
                                </a>
                            </li>
                            <li className="flex items-start gap-3 text-white/70">
                                <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                                <span>North Wiltshire and the surrounding areas</span>
                            </li>
                        </ul>
                        <h4 className="text-lg font-semibold mt-6 mb-3 font-serif">Service Areas</h4>
                        <div className="flex flex-wrap gap-2">
                            {['chippenham', 'swindon', 'bath', 'tetbury', 'corsham', 'cirencester'].map(area => (
                                <Link 
                                    key={area}
                                    to={`/mantrailing-near/${area}`}
                                    className="text-white/50 hover:text-primary text-xs transition-colors"
                                >
                                    {area.charAt(0).toUpperCase() + area.slice(1)}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-white/50 text-sm">
                        © {currentYear} Craven Canines. All rights reserved.
                    </p>
                    <div className="flex gap-6 text-sm">
                        <Link to="/privacy" className="text-white/50 hover:text-white transition-colors">
                            Privacy Policy
                        </Link>
                        <Link to="/terms" className="text-white/50 hover:text-white transition-colors">
                            Terms of Service
                        </Link>
                        <a 
                            href="https://customer-assets.emergentagent.com/job_eaf279a0-83eb-443a-a424-79ded8f717a5/artifacts/hne32jxn_Craven%20Canines%20Risk%20Assessment.pdf" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-white/50 hover:text-white transition-colors"
                        >
                            Risk Assessment
                        </a>
                    </div>
                    <p className="text-white/50 text-xs flex items-center gap-1">
                        <span>🔒</span> All payments securely processed
                    </p>
                </div>

                {/* Certification Badge */}
                <div className="border-t border-white/10 mt-8 pt-8 flex justify-center">
                    <a 
                        href="https://www.scentworkuk.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:opacity-80 transition-opacity"
                        data-testid="scentwork-uk-badge"
                    >
                        <img 
                            src="https://customer-assets.emergentagent.com/job_c2878503-a6d2-4939-a7a9-e7b569525cc6/artifacts/bdi6g9g7_Scentwork%20UK%C2%AE.png" 
                            alt="Scentwork UK Certified" 
                            className="h-24 w-auto"
                        />
                    </a>
                </div>
            </div>
        </footer>
    );
};
